/**
 * Data List Component.
 * It helps to display as Simple Data List with items.
 * Items can be paginated with `previous` and `next` link.
 */

import React, { Component } from 'react';
import PropTypes from 'prop-types';
import Page from '../../../lib/page';

// Simple Component to evaluate the HTML String.
const createMarkup = (message) => {
  const innerHTML = Page.getHTMLElement({ message, useHTML: true });
  return message ? (
    <p className="article-excerpt">{innerHTML}</p>
  ) : null;
};

class DataList extends Component {
  constructor(props) {
    super(props);

    if (props.paginate) {
      this.state = {
        currentPage: 1,
        pageCount: Math.ceil(props.items.length / props.pageSize),
      };
    }
  }

  componentWillReceiveProps(props) {
    this.setState({
      currentPage: 1,
      pageCount: Math.ceil(props.items.length / props.pageSize),
    });
  }

  // This Method helps to return list of items.
  getListItems() {
    const endIndex = (this.state.currentPage * this.props.pageSize);
    const beginIndex = (endIndex - this.props.pageSize);
    const items = this.props.items.slice(beginIndex, endIndex);

    /* eslint-disable react/no-danger */
    return (
      <ul className="list-items">
        {items.map((item, index) => {
          const itemIndex = `item-${index}`;

          return (<li key={itemIndex}>
            <a href={item.link}>
              {item.title}
              <p>{createMarkup(item.description)}</p>
            </a>
          </li>);
        })}
      </ul>
    );
  }

  getPaginationClass() {
    if (this.props.showGoToPage) {
      return 'paginator';
    }
    return 'paginator no-page-links';
  }

  // This Method helps to generate `paging` as per Record Count.
  getPagingItems() {
    if (this.props.paginate && this.props.pageSize > 0 && this.props.showGoToPage) {
      const pageCount = this.state.pageCount;
      const items = new Array(pageCount).fill('');

      return items.map((no, i) => {
        const randomNumber = Math.round(Math.random() * 123);
        const page = i + 1;
        let pageItem = <span>{page}</span>;

        if (this.state.currentPage !== page) {
          pageItem = <a href={`#go-to-page-${page}`} onClick={e => this.goToPage(e, page)}>{page}</a>;
        }

        return (
          <li key={randomNumber}>
            {pageItem}
          </li>
        );
      });
    }
    return null;
  }

  // Previous Label for pagination
  getNextLinLabel() {
    if (this.props.showGoToPage) {
      return this.props.labels.next;
    }
    return (
      <span className="pagination-label">
        {this.props.labels.next}
        <span className="vx_icon vx_icon-small vx_icon-arrow-right-half-small" />
      </span>
    );
  }

  // Next Label for pagination
  getPrevLinkLabel() {
    if (this.props.showGoToPage) {
      return this.props.labels.prev;
    }
    return (
      <span className="pagination-label">
        <span className="vx_icon vx_icon-small vx_icon-arrow-left-half-small" />
        {this.props.labels.prev}
      </span>
    );
  }

  // Return `Previous` Link in Pagination.
  getPreviousLink() {
    if (this.goPreviousValidation()) {
      return (
        <li>
          <a href="#go-to-previous" onClick={e => this.goToPrevious(e)}>{this.getPrevLinkLabel()}</a>
        </li>
      );
    }
    return null;
  }

  // Return `Next` Link in Pagination.
  getNextLink() {
    if (this.goNextValidation()) {
      return (
        <li>
          <a href="#go-to-next" onClick={e => this.goToNext(e)}>{this.getNextLinLabel()}</a>
        </li>
      );
    }
    return null;
  }

  // Return Pagination Block.
  getPaginator() {
    if (!this.goPreviousValidation() && !this.goNextValidation()) {
      return null;
    }
    return (
      <div className="search-result-pagination">
        <div className={this.getPaginationClass()}>
          <ul className="pagination">
            {this.getPreviousLink()}
            {this.getPagingItems()}
            {this.getNextLink()}
          </ul>
        </div>
      </div>
    );
  }

  // Validation for showing 'Previous'
  goPreviousValidation() {
    return this.state.currentPage > 1 && this.state.pageCount > 0;
  }

  // Validation for showing 'Next'
  goNextValidation() {
    return this.state.currentPage < this.state.pageCount;
  }

  // Triggers, When `page numbers` link clicked in pagination.
  goToPage(e, page) {
    e.preventDefault();
    this.setState({
      currentPage: page,
    });
    this.props.onClick.pageNumber(page);
  }

  // Triggers, When `previous` link clicked in pagination.
  goToPrevious(e) {
    e.preventDefault();
    const page = (this.state.currentPage - 1);
    this.setState({
      currentPage: page,
    });
    this.props.onClick.previous(page);
  }

  // Triggers, When `next` link clicked in pagination.
  goToNext(e) {
    e.preventDefault();
    const page = (this.state.currentPage + 1);
    this.setState({
      currentPage: page,
    });
    this.props.onClick.next(page);
  }

  // Render the `Panel` Component.
  render() {
    return (
      <div className={`${this.props.parentClass} data-list`}>
        {this.getListItems()}
        {this.getPaginator()}
      </div>
    );
  }
}

// DataList Props Types.
DataList.propTypes = {
  // Simple Props.
  parentClass: PropTypes.string,
  paginate: PropTypes.bool,
  pageSize: PropTypes.number.isRequired,
  showGoToPage: PropTypes.bool,

  // Complex Props.
  items: PropTypes.arrayOf(PropTypes.shape({
    title: PropTypes.string,
    link: PropTypes.string,
    description: PropTypes.string,
  })).isRequired,

  labels: PropTypes.shape({
    prev: PropTypes.string,
    next: PropTypes.string,
  }).isRequired,

  onClick: PropTypes.shape({
    next: PropTypes.func,
    previous: PropTypes.func,
    pageNumber: PropTypes.func,
  }),
};

// DataList Props default values.
DataList.defaultProps = {
  parentClass: '',
  paginate: false,
  pageSize: 10,
  onClick: {
    previous: () => {},
    pageNumber: () => {},
    next: () => {},
  },
  showGoToPage: true,
};

export default DataList;
